import React, { useState, useEffect, useReducer, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Store as StoreIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import * as pharmaApi from "../../services/pharmaApi";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_STORES":
      return [...action.payload];
    case "UPDATE_STORE":
      const store = action.payload;
      const storeIndex = state.findIndex((s) => s.id === store.id);
      if (storeIndex !== -1) {
        state[storeIndex] = store;
        return [...state];
      }
      return [store, ...state];
    case "DELETE_STORE":
      return state.filter((s) => s.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  inactiveChip: {
    backgroundColor: "#9e9e9e",
    color: "#fff",
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
}));

const initialStoreState = {
  name: "",
  cnpj: "",
  phone: "",
  email: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
  zipcode: "",
  deliveryEnabled: true,
  pickupEnabled: true,
  minOrderValue: 0,
  deliveryFee: 0,
  deliveryRadius: 10,
  isActive: true,
};

const PdvStores = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [stores, dispatch] = useReducer(reducer, []);
  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingStore, setDeletingStore] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [storeData, setStoreData] = useState(initialStoreState);

  useEffect(() => {
    const fetchStores = async () => {
      setLoading(true);
      try {
        // Use PharmaFlow API
        const data = await pharmaApi.getStores({ search: searchParam });
        dispatch({ type: "LOAD_STORES", payload: data || [] });
        setHasMore(false); // PharmaFlow returns all stores
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchStores();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const onPdvStore = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_STORE", payload: data.store });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_STORE", payload: data.storeId });
      }
    };

    socket.on(`company-${companyId}-pdvStore`, onPdvStore);
    return () => {
      socket.off(`company-${companyId}-pdvStore`, onPdvStore);
    };
  }, [socket, user]);

  const handleOpenModal = (store = null) => {
    if (store) {
      setEditingStore(store);
      setStoreData({
        name: store.name || "",
        cnpj: store.cnpj || "",
        phone: store.phone || "",
        email: store.email || "",
        street: store.street || "",
        number: store.number || "",
        complement: store.complement || "",
        neighborhood: store.neighborhood || "",
        city: store.city || "",
        state: store.state || "",
        zipcode: store.zipcode || "",
        deliveryEnabled: store.deliveryEnabled ?? true,
        pickupEnabled: store.pickupEnabled ?? true,
        minOrderValue: store.minOrderValue || 0,
        deliveryFee: store.deliveryFee || 0,
        deliveryRadius: store.deliveryRadius || 10,
        isActive: store.isActive ?? true,
      });
    } else {
      setEditingStore(null);
      setStoreData(initialStoreState);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingStore(null);
    setStoreData(initialStoreState);
  };

  const handleSaveStore = async () => {
    try {
      // Use PharmaFlow API
      if (editingStore) {
        const updated = await pharmaApi.updateStore(editingStore.id, storeData);
        dispatch({ type: "UPDATE_STORE", payload: updated });
        toast.success("Loja atualizada com sucesso!");
      } else {
        const created = await pharmaApi.createStore(storeData);
        dispatch({ type: "UPDATE_STORE", payload: created });
        toast.success("Loja criada com sucesso!");
      }
      handleCloseModal();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteStore = async (storeId) => {
    try {
      // Use PharmaFlow API
      await pharmaApi.deleteStore(storeId);
      dispatch({ type: "DELETE_STORE", payload: storeId });
      toast.success("Loja removida com sucesso!");
      setConfirmOpen(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setStoreData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>OtimizAPP Pharma - Lojas</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Nova Loja
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <Box className={classes.filterContainer}>
          <TextField
            placeholder="Buscar loja..."
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            style={{ minWidth: 300 }}
          />
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>CNPJ</TableCell>
              <TableCell>Cidade/UF</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>Delivery</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={7} />
            ) : (
              stores.map((store) => (
                <TableRow key={store.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <StoreIcon color="action" />
                      {store.name}
                    </Box>
                  </TableCell>
                  <TableCell>{store.cnpj || "-"}</TableCell>
                  <TableCell>
                    {store.city && store.state
                      ? `${store.city}/${store.state}`
                      : "-"}
                  </TableCell>
                  <TableCell>{store.phone || "-"}</TableCell>
                  <TableCell>
                    {store.deliveryEnabled ? (
                      <Chip label="Sim" size="small" color="primary" />
                    ) : (
                      <Chip label="Não" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={store.isActive ? "Ativa" : "Inativa"}
                      size="small"
                      className={store.isActive ? classes.activeChip : classes.inactiveChip}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenModal(store)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingStore(store);
                        setConfirmOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Store Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingStore ? "Editar Loja" : "Nova Loja"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome da Loja"
                fullWidth
                variant="outlined"
                value={storeData.name}
                onChange={handleChange("name")}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="CNPJ"
                fullWidth
                variant="outlined"
                value={storeData.cnpj}
                onChange={handleChange("cnpj")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Telefone"
                fullWidth
                variant="outlined"
                value={storeData.phone}
                onChange={handleChange("phone")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="E-mail"
                fullWidth
                variant="outlined"
                value={storeData.email}
                onChange={handleChange("email")}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Rua"
                fullWidth
                variant="outlined"
                value={storeData.street}
                onChange={handleChange("street")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Número"
                fullWidth
                variant="outlined"
                value={storeData.number}
                onChange={handleChange("number")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Complemento"
                fullWidth
                variant="outlined"
                value={storeData.complement}
                onChange={handleChange("complement")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bairro"
                fullWidth
                variant="outlined"
                value={storeData.neighborhood}
                onChange={handleChange("neighborhood")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Cidade"
                fullWidth
                variant="outlined"
                value={storeData.city}
                onChange={handleChange("city")}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Estado"
                fullWidth
                variant="outlined"
                value={storeData.state}
                onChange={handleChange("state")}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="CEP"
                fullWidth
                variant="outlined"
                value={storeData.zipcode}
                onChange={handleChange("zipcode")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Pedido Mínimo (R$)"
                fullWidth
                variant="outlined"
                type="number"
                value={storeData.minOrderValue}
                onChange={handleChange("minOrderValue")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Taxa de Entrega (R$)"
                fullWidth
                variant="outlined"
                type="number"
                value={storeData.deliveryFee}
                onChange={handleChange("deliveryFee")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Raio de Entrega (km)"
                fullWidth
                variant="outlined"
                type="number"
                value={storeData.deliveryRadius}
                onChange={handleChange("deliveryRadius")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={storeData.deliveryEnabled}
                    onChange={handleChange("deliveryEnabled")}
                    color="primary"
                  />
                }
                label="Delivery Ativo"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={storeData.pickupEnabled}
                    onChange={handleChange("pickupEnabled")}
                    color="primary"
                  />
                }
                label="Retirada Ativa"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={storeData.isActive}
                    onChange={handleChange("isActive")}
                    color="primary"
                  />
                }
                label="Loja Ativa"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button onClick={handleSaveStore} color="primary" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={confirmOpen}
        title="Excluir Loja"
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => handleDeleteStore(deletingStore?.id)}
      >
        Deseja realmente excluir a loja "{deletingStore?.name}"?
      </ConfirmationModal>
    </MainContainer>
  );
};

export default PdvStores;
